import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import API_BASE_URL from "../config";
import "./Auth.css";

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const API_BASE = `${API_BASE_URL}/auth`;

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tab") === "signup" ? "signup" : "login";
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("signup-form");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Проверяем авторизацию через API
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API_BASE}/check`, {
          credentials: 'include' // Важно: отправляем cookies
        });
        const data = await res.json();
        if (data.authenticated) {
          navigate("/");
          return;
        }
      } catch (err) {
        console.error("Auth check failed:", err);
      }

      // Проверяем незавершенную регистрацию
      const storedEmail = sessionStorage.getItem("registrationEmail");
      if (storedEmail) {
        setEmail(storedEmail);
        setStep("code-verify");
        setActiveTab("signup");
      }
    };

    checkAuth();
  }, [navigate, API_BASE]);

  // Переключаем вкладку по query-параметру tab
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam === "signup") {
      setActiveTab("signup");
      setStep("signup-form");
    }
    if (tabParam === "login") {
      setActiveTab("login");
      resetSignupState();
    }
  }, [location.search]);

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Invalid email format";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must contain at least 8 characters";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include', // Важно: включаем cookies
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setErrors({ 
          email: data.detail || "Invalid email or password", 
          password: "Invalid email or password" 
        });
        return;
      }

      // JWT теперь в httpOnly cookie, не нужно сохранять в localStorage
      if (data?.authenticated) {
        // Отправляем событие для обновления состояния в других компонентах
        window.dispatchEvent(new Event("authChange"));
        navigate("/");
      }
    } catch (err) {
      setErrors({ email: "An error occurred during login. Please try again later." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Invalid email format";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must contain at least 8 characters";
    } else if (!/[A-Za-z]/.test(password)) {
      newErrors.password = "Password must contain at least one letter";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirm password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include', // Важно: включаем cookies
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setErrors({ email: data.detail || "Registration error" });
        return;
      }

      sessionStorage.setItem("registrationEmail", email);
      setStep("code-verify");
      setActiveTab("signup");
      setErrors({});
    } catch (err) {
      setErrors({ email: "An error occurred during registration. Please try again later." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const newErrors = {};

    const registrationEmail = sessionStorage.getItem("registrationEmail") || email;
    if (!registrationEmail) {
      setErrors({ code: "Email not found. Please start registration again." });
      setStep("signup-form");
      return;
    }

    if (!code) {
      newErrors.code = "Confirmation code is required";
    } else if (code.trim().length !== 6) {
      newErrors.code = "Code must contain 6 characters";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include', // Важно: включаем cookies
        body: JSON.stringify({ email: registrationEmail, code }),
      });

      const data = await res.json();

      if (!res.ok) {
        setErrors({ code: data.detail || "Invalid code" });
        return;
      }

      // JWT теперь в httpOnly cookie, не нужно сохранять в localStorage
      if (data?.authenticated) {
        sessionStorage.removeItem("registrationEmail");
        window.dispatchEvent(new Event("authChange"));
        navigate("/");
      }
    } catch (err) {
      setErrors({ code: "An error occurred during confirmation. Please try again later." });
    } finally {
      setIsLoading(false);
    }
  };

  const resetSignupState = () => {
    setStep("signup-form");
    setCode("");
    setErrors({});
    sessionStorage.removeItem("registrationEmail");
  };

  return (
    <div className="auth-page">
      {/* Header */}
      <header className="auth-header">
        <Link to="/" className="auth-header-brand">
          <img src="/logo.png" alt="SoliditySet Logo" className="auth-header-icon" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
          <span className="auth-header-title">
            SoliditySet
          </span>
        </Link>
      </header>

      {/* Main Content */}
      <div className="auth-main">
        <div className="auth-card-wrapper">
          <div className="auth-card">
            <div className="auth-card-header">
              <h1 className="auth-card-title">
                Welcome to SoliditySet
              </h1>
              <p className="auth-card-subtitle">
                Start your Web3 development journey
              </p>
            </div>

            <Tabs
              value={activeTab}
              onValueChange={(val) => {
                setActiveTab(val);
                setErrors({});
                if (val === "signup") {
                  setStep("signup-form");
                } else {
                  resetSignupState();
                }
              }}
              className="auth-tabs"
            >
              <TabsList className="auth-tabs-list">
                <TabsTrigger 
                  value="login"
                  className="auth-tab-trigger"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="signup"
                  className="auth-tab-trigger"
                >
                  Sign Up
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="auth-form">
                  <div className="auth-form-field">
                    <Label htmlFor="login-email" className="auth-label">
                      Email
                    </Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors({ ...errors, email: "" });
                      }}
                      className="auth-input"
                      required
                    />
                    {errors.email && <p className="auth-error">{errors.email}</p>}
                  </div>

                  <div className="auth-form-field">
                    <Label htmlFor="login-password" className="auth-label">
                      Password
                    </Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors({ ...errors, password: "" });
                      }}
                      className="auth-input"
                      required
                    />
                    {errors.password && <p className="auth-error">{errors.password}</p>}
                  </div>

                  <div style={{ textAlign: "right", marginBottom: "0.5rem" }}>
                    <button
                      type="button"
                      onClick={() => navigate("/reset-password")}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#8b5cf6",
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontSize: "0.875rem",
                        padding: 0,
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>

                  <Button 
                    type="submit" 
                    className="auth-button"
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                {step === "signup-form" && (
                  <form onSubmit={handleSignup} className="auth-form">
                    <div className="auth-form-field">
                      <Label htmlFor="signup-email" className="auth-label">
                        Email
                      </Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errors.email) setErrors({ ...errors, email: "" });
                        }}
                        className="auth-input"
                        required
                      />
                      {errors.email && <p className="auth-error">{errors.email}</p>}
                    </div>

                    <div className="auth-form-field">
                      <Label htmlFor="signup-password" className="auth-label">
                        Password
                      </Label>
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder="Create a password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (errors.password) setErrors({ ...errors, password: "" });
                        }}
                        className="auth-input"
                        required
                      />
                      <p className="auth-hint">At least 8 characters, include a letter.</p>
                      {errors.password && <p className="auth-error">{errors.password}</p>}
                    </div>

                    <div className="auth-form-field">
                      <Label htmlFor="confirm-password" className="auth-label">
                        Confirm Password
                      </Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: "" });
                        }}
                        className="auth-input"
                        required
                      />
                      {errors.confirmPassword && (
                        <p className="auth-error">{errors.confirmPassword}</p>
                      )}
                    </div>

                    <Button 
                      type="submit" 
                      className="auth-button"
                      disabled={isLoading}
                    >
                      {isLoading ? "Creating..." : "Create Account"}
                    </Button>
                  </form>
                )}

                {step === "code-verify" && (
                  <form onSubmit={handleVerifyCode} className="auth-form">
                    <div className="auth-form-field">
                      <Label htmlFor="verify-email" className="auth-label">
                        Email
                      </Label>
                      <Input
                        id="verify-email"
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errors.email) setErrors({ ...errors, email: "" });
                        }}
                        className="auth-input"
                        required
                      />
                      {errors.email && <p className="auth-error">{errors.email}</p>}
                    </div>

                    <div className="auth-form-field">
                      <Label htmlFor="verify-code" className="auth-label">
                        Verification Code
                      </Label>
                      <Input
                        id="verify-code"
                        type="text"
                        maxLength={6}
                        placeholder="Enter 6-digit code"
                        value={code}
                        onChange={(e) => {
                          const value = e.target.value.slice(0, 6);
                          setCode(value.trim());
                          if (errors.code) setErrors({ ...errors, code: "" });
                        }}
                        className="auth-input"
                        required
                      />
                      {errors.code && <p className="auth-error">{errors.code}</p>}
                    </div>

                    <Button 
                      type="submit" 
                      className="auth-button"
                      disabled={isLoading || code.trim().length !== 6}
                    >
                      {isLoading ? "Verifying..." : "Verify Email"}
                    </Button>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      className="auth-back-button"
                      onClick={resetSignupState}
                    >
                      Start over
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;

